import React, { useState } from 'react';
import { Button, Flex, Input, List, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { ChannelCategoryEntity } from '../../../domain/entities/ChannelCategory';

interface ChannelCategoriesModalProps {
  open: boolean;
  categories: ChannelCategoryEntity[];
  isSaving: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<boolean>;
  onRename: (uuid: string, name: string) => Promise<boolean>;
}

const CategoryRow: React.FC<{
  category: ChannelCategoryEntity;
  isSaving: boolean;
  onRename: (uuid: string, name: string) => Promise<boolean>;
}> = ({ category, isSaving, onRename }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(category.name);
  const isChanged = name.trim() !== category.name && name.trim() !== '';

  return (
    <List.Item
      actions={[
        <Button key="save" type="link" disabled={!isChanged} loading={isSaving && isChanged} onClick={() => onRename(category.uuid, name)}>
          {t('save', { ns: 'buttons' })}
        </Button>,
      ]}
    >
      <Flex vertical gap={4} style={{ width: '100%' }}>
        <Input value={name} onChange={(e) => setName(e.target.value)} onPressEnter={() => isChanged && onRename(category.uuid, name)} />
        <Typography.Text type="secondary">
          {t('channel-category-channels-count', { ns: 'labels', count: category.channelsCount ?? 0 })}
        </Typography.Text>
      </Flex>
    </List.Item>
  );
};

const ChannelCategoriesModal: React.FC<ChannelCategoriesModalProps> = ({ open, categories, isSaving, onClose, onCreate, onRename }) => {
  const { t } = useTranslation();
  const [newName, setNewName] = useState('');

  const handleCreate = async () => {
    if (await onCreate(newName)) setNewName('');
  };

  return (
    <Modal
      open={open}
      title={t('channel-categories', { ns: 'titles' })}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Flex gap={8} style={{ marginBottom: 16 }}>
        <Input
          value={newName}
          placeholder={t('channel-category-new-placeholder', { ns: 'labels' })}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={handleCreate}
        />
        <Button type="primary" disabled={!newName.trim()} loading={isSaving && Boolean(newName.trim())} onClick={handleCreate}>
          {t('add-category', { ns: 'buttons' })}
        </Button>
      </Flex>
      <List
        dataSource={categories}
        rowKey={(category) => category.uuid}
        locale={{ emptyText: t('no-items', { ns: 'labels' }) }}
        renderItem={(category) => <CategoryRow key={`${category.uuid}:${category.name}`} category={category} isSaving={isSaving} onRename={onRename} />}
      />
    </Modal>
  );
};

export default ChannelCategoriesModal;
